import { useContext } from 'react';

import { AgentChatContext } from '@/components/agent-chat/agent-chat-provider';
import { cn } from '@/lib/utils';
import {
  ArtifactCard,
  ArtifactCardSkeleton,
} from '@/components/artifacts/artifact-card';
import type { ArtifactFile, ArtifactListItem } from '@/types/artifacts';
import { openExternalUrl } from '@/utils/url-utils';

export interface ChatArtifactCardModel {
  key: string;
  /** Ready for `ArtifactCard`; null only while a promoted row resolves. */
  item: ArtifactListItem | null;
  primaryFile?: ArtifactFile;
  /** Set once the file has a persisted Artifact row. */
  artifactId?: string;
  /** Direct link for files that only ever existed on the stream. */
  downloadUrl?: string;
  /** Why the file cannot be opened, when it cannot be. */
  unavailableReason?: string;
  /** Revision context, for a file the agent produced a new version of. */
  revision?: { label: string; changeSummary?: string };
}

/**
 * Past this many files the thumbnails cost more room than they earn, so the set
 * collapses to rows.
 */
const LIST_MODE_THRESHOLD = 2;

/** Rows the chat already holds, which need no resolving to render. */
export function promotedArtifactCards(
  artifacts: ArtifactListItem[],
): ChatArtifactCardModel[] {
  return artifacts.map((artifact) => ({
    key: artifact.id,
    item: artifact,
    artifactId: artifact.id,
  }));
}

/**
 * The files a chat delivered: previews while there are few enough to show, rows
 * once there are not.
 *
 * `onOpen` takes precedence over the agent chat's own artifact surface, for
 * callers that host their own viewer.
 */
export function ChatArtifactCards({
  cards,
  onOpen,
}: {
  cards: ChatArtifactCardModel[];
  onOpen?: (artifact: ArtifactListItem) => void;
}) {
  const context = useContext(AgentChatContext);

  if (cards.length === 0) return null;

  const viewMode = cards.length > LIST_MODE_THRESHOLD ? 'list' : 'grid';

  const openHandlerFor = (card: ChatArtifactCardModel) => {
    if (onOpen) return onOpen;
    const openArtifact = context?.onOpenArtifact;
    if (card.artifactId && openArtifact) {
      return () => openArtifact(card.artifactId!);
    }
    // A promoted artifact with no chat surface falls through to the card's own
    // navigation, which opens the artifact detail page.
    if (card.artifactId) return undefined;
    if (card.downloadUrl) {
      return () => openExternalUrl(card.downloadUrl);
    }
    return () => undefined;
  };

  return (
    // The cards measure themselves against this wrapper rather than the
    // viewport: the same chat renders in a narrow rail and in a full-width
    // page, and only the element itself knows which one it is.
    <div className='@container w-full'>
      <div
        className={cn(
          viewMode === 'list'
            ? 'flex flex-col gap-2'
            : cn(
                // An implicit `auto` track sizes to the card's max-content
                // width and overflows a narrow rail, so the single-column
                // case names its track too. Side-by-side cards need room
                // to stay legible, which a chat rail does not have.
                // `items-start` overrides the grid default of stretching
                // every row to its tallest cell -- without it, a revision
                // badge under one card would inflate its taller sibling to
                // match, leaving empty space below that card's footer.
                'grid grid-cols-1 items-start gap-3',
                cards.length > 1 && '@sm:grid-cols-2',
              ),
        )}
      >
        {cards.map((card) =>
          card.item ? (
            <ArtifactCard
              key={card.key}
              artifact={card.item}
              primaryFile={card.primaryFile}
              viewMode={viewMode}
              onOpen={openHandlerFor(card)}
              revision={card.revision}
              unavailableReason={card.unavailableReason}
              hideMenu
            />
          ) : (
            <ArtifactCardSkeleton key={card.key} viewMode={viewMode} />
          ),
        )}
      </div>
    </div>
  );
}
